import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Wallet, FileDown, FileSpreadsheet } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { downloadReportPdf } from "@/lib/pdfReport";
import { downloadReportXlsx } from "@/lib/xlsxReport";
import { Button } from "@/components/ui/button";

const PALETTE = ["#0055FF", "#059669", "#D97706", "#0F172A", "#64748B", "#DC2626", "#0EA5E9", "#10B981"];

function Tip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 shadow-lg">
      {label && <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{label}</p>}
      {payload.map((p) => (
        <p key={p.dataKey || p.name} className="tnum mt-1 text-xs font-medium" style={{ color: p.payload?.fill || p.fill || "#0F172A" }}>
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
}

export default function Reports() {
  const { t, fmt, monthLabel } = useI18n();
  const [r, setR] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => { api.get("/reports").then((res) => setR(res.data)).catch(() => {}); }, []);

  if (!r) return <p className="py-24 text-center text-sm text-slate-400" data-testid="reports-loading">{t.app.loading}</p>;

  const monthly = r.monthly.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const totals = [
    { id: "income", label: t.rep.totalIncome, v: r.total_income, cls: "text-positive", icon: <ArrowUpRight className="h-4 w-4" /> },
    { id: "expense", label: t.rep.totalExpense, v: r.total_expense, cls: "text-negative", icon: <ArrowDownRight className="h-4 w-4" /> },
    { id: "net", label: t.rep.totalNet, v: r.total_income - r.total_expense, cls: "text-slate-900", icon: <Wallet className="h-4 w-4" /> },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex justify-end gap-2" data-testid="reports-toolbar">
        <Button variant="outline" onClick={() => downloadReportXlsx({ t, data: r, monthLabel })} data-testid="reports-excel-button">
          <FileSpreadsheet className="mr-2 h-4 w-4" />{t.pdf.excel}
        </Button>
        <Button variant="outline" onClick={async () => { setPdfBusy(true); try { await downloadReportPdf({ t, fmt, data: r, monthLabel }); } finally { setPdfBusy(false); } }}
          disabled={pdfBusy} data-testid="reports-pdf-button">
          <FileDown className="mr-2 h-4 w-4" />{pdfBusy ? t.pdf.preparing : t.pdf.download}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {totals.map((k) => (
          <div key={k.id} data-testid={`report-total-${k.id}-card`}
            className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-slate-400">
              {k.icon}
              <p className="text-[11px] font-medium uppercase tracking-wider">{k.label}</p>
            </div>
            <p className={`tnum mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl ${k.cls}`}>{fmt(k.v)}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="report-monthly-chart">
        <p className="font-display text-sm font-semibold text-slate-900">{t.rep.monthly}</p>
        <div className="mt-5 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={{ top: 5, right: 5, left: -8, bottom: 0 }} barGap={3}>
              <CartesianGrid strokeDasharray="3 5" stroke="#0F172A" strokeOpacity={0.07} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip fmt={fmt} />} cursor={{ fill: "rgba(15,23,42,0.03)" }} />
              <Bar dataKey="income" name={t.app.income} fill="#0055FF" radius={[4, 4, 0, 0]} maxBarSize={26} />
              <Bar dataKey="expense" name={t.app.expense} fill="#DC2626" radius={[4, 4, 0, 0]} maxBarSize={26} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="report-expense-chart">
          <p className="font-display text-sm font-semibold text-slate-900">{t.rep.expCat}</p>
          {r.expense_categories.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-400">{t.app.noData}</p>
          ) : (
            <>
              <div className="mx-auto mt-4 h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={r.expense_categories} dataKey="amount" nameKey="category" innerRadius={52} outerRadius={80} paddingAngle={2} strokeWidth={0}>
                      {r.expense_categories.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip content={<Tip fmt={fmt} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-1.5">
                {r.expense_categories.slice(0, 5).map((c, i) => (
                  <div key={c.category} className="flex items-center gap-2.5 text-xs">
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                    <span className="text-slate-600">{c.category}</span>
                    <span className="tnum ml-auto font-mono font-medium text-slate-900">{fmt(c.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="report-income-list">
          <p className="font-display text-sm font-semibold text-slate-900">{t.rep.incCat}</p>
          <div className="mt-5 space-y-4">
            {r.income_categories.map((c, i) => {
              const max = r.income_categories[0]?.amount || 1;
              return (
                <div key={c.category}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">{c.category}</span>
                    <span className="tnum font-mono font-medium text-slate-900">{fmt(c.amount)}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${(c.amount / max) * 100}%`, opacity: 1 - i * 0.12 }} />
                  </div>
                </div>
              );
            })}
            {r.income_categories.length === 0 && <p className="py-10 text-center text-sm text-slate-400">{t.app.noData}</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="report-cari-list">
          <p className="font-display text-sm font-semibold text-slate-900">{t.rep.topCari}</p>
          <div className="mt-5 space-y-2.5">
            {r.top_receivables.length === 0 && <p className="py-10 text-center text-sm text-slate-400">{t.rep.noCari}</p>}
            {r.top_receivables.map((c, i) => (
              <div key={c.cari_id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-4 py-3">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-900 font-mono text-[10px] text-white">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="truncate text-sm text-slate-700">{c.cari_name}</p>
                <p className="tnum ml-auto shrink-0 font-mono text-sm font-medium text-slate-900">{fmt(c.amount)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
